// Centralized UI strings — every user-facing string in the app, in English and
// Simplified Chinese. To switch the whole app's language, change the LANG
// constant below and rebuild.
//
// Structure:
//   - EN is the source of truth for the shape (key set + function signatures).
//   - ZH is type-checked against `typeof EN`, so a missing/renamed/mistyped key
//     is a compile error.
//   - Strings that interpolate runtime values are functions; a few that
//     interleave non-translatable <code> nodes return React fragments (hence
//     this file is .tsx).
//
// Do NOT use `as const` here — string properties must widen to `string` so the
// two language tables share one type.

import type * as React from 'react';

export type Lang = 'en' | 'zh';

// ─────────────────────────────────────────────────────────────────────────────
// The whole app's language. Change DEPLOY_LANG ('en' or 'zh') and rebuild.
//
// The unit tests assert against the English copy, so we pin English under
// Vitest — otherwise switching the deployed language would break tests that
// have nothing to do with translation. This does NOT affect the running app.
// ─────────────────────────────────────────────────────────────────────────────
const DEPLOY_LANG: Lang = 'zh';

const metaEnv = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
const RUNNING_TESTS = metaEnv?.MODE === 'test' || Boolean(metaEnv?.VITEST);

export const LANG: Lang = RUNNING_TESTS ? 'en' : DEPLOY_LANG;

const EN = {
  // ── AnnotateView / TopBar ──
  topBar: {
    quit: 'Quit',
    cancel: 'Cancel',
    freeDrag: 'Free Drag',
    freeDragTitle: 'Toggle free drag (Shift)',
    skeleton: 'Skeleton',
    unsavedTitle: 'Unsaved edits',
    unsavedDesc: 'You have unsaved edits. Quit anyway?',
    v2Badge: 'secPose v2 · carry-forward',
    v2BadgeTitle:
      'No predictions were loaded. The first frame starts from a default pose; later frames carry forward from the previous frame until edited or saved.',
  },

  // ── AnnotateView / ProgressBar ──
  progress: {
    ariaLabel: 'Frame progress',
    totalTime: 'Total time:',
    totalFrames: 'Total Frames:',
    totalPickedFrames: 'Total Picked Frames:',
    unannotatedFrames: 'Unannotated frames:',
    annotatedUnsavedFrames: 'Modified Frames:',
    annotatedSavedFrames: 'Saved Clean Frames:',
  },

  // ── AnnotateView / SubmitButton ──
  submit: {
    submit: 'Submit',
    saving: 'Saving…',
    ok: 'OK',
    errorPrefix: 'Error: ',
    cannotTitle: 'Cannot submit yet',
    cannotDesc: (done: number, total: number): string =>
      `Not all frames have been audited (${done} of ${total} done). Click Next through every frame before submitting.`,
  },

  // ── AnnotateView / NextButton ──
  nextBtn: {
    next: 'Save Current Frame',
    back: 'Cancel',
    submit: 'Submit',
    saving: 'Saving…',
    errorPrefix: 'Error: ',
    completionTitle: 'All frames annotated',
    completionDesc: 'All the frames have been annotated. Do you want to submit the results?',
  },

  // ── AnnotateView / PendingButton ──
  pending: {
    pending: 'Pending',
    saving: 'Saving…',
  },

  draftTransfer: {
    exportTitle: 'Export encrypted draft',
    importTitle: 'Import draft',
    exportDialogTitle: 'Export encrypted draft',
    exportDialogDesc: 'Keep the recovery code separately. It cannot be recovered by the platform.',
    recoveryCode: 'Recovery code',
    copyCode: 'Copy recovery code',
    codeCopied: 'Recovery code copied',
    codeSaved: 'I saved the recovery code',
    download: 'Download encrypted draft',
    exported: 'Encrypted draft downloaded',
    importDialogTitle: 'Restore local draft',
    importDialogDesc: 'The file is decrypted and checked only in this browser.',
    chooseFile: 'Choose draft file',
    enterCode: 'Enter recovery code',
    validate: 'Validate draft',
    validating: 'Validating…',
    importedDraft: 'Imported draft',
    currentDraft: 'Current local draft',
    noCurrentDraft: 'No current local draft',
    savedAt: 'Saved at',
    elapsed: 'Elapsed',
    editedFrames: 'Edited frames',
    sourceVerified: 'Source fingerprints verified',
    legacyWarning:
      'Legacy unencrypted draft: operator identity and source fingerprints cannot be verified. Check key frames before submitting.',
    overwriteWarning: 'Restoring will replace the current local draft and editor state.',
    cancel: 'Cancel',
    restore: 'Use imported draft',
    restoring: 'Restoring…',
    restored: 'SecPose draft restored locally',
    leaseRequired: 'Claim this Episode before importing or submitting.',
  },

  // ── AnnotateView / KeypointLegend ──
  legend: {
    ariaLabel: 'Keypoint color legend',
    shoulder: 'Shoulder',
    elbow: 'Elbow',
    pelvis: 'Pelvis',
    leftLine: 'Left line',
    rightLine: 'Right line',
  },

  // ── Keypoint display names (SidePanel roster). Keys are the stored
  //    KeypointName values; only the label is translated. ──
  keypoints: {
    left_shoulder: 'left shoulder',
    right_shoulder: 'right shoulder',
    left_elbow: 'left elbow',
    right_elbow: 'right elbow',
    pelvis: 'pelvis',
  },

  // ── Camera view names. Keys are the stored Side values; only the label is
  //    translated. Shown in the per-view label bar and in side-referencing
  //    messages/aria. ──
  views: {
    front_left: 'HEAD FRONT LEFT',
    front_right: 'HEAD FRONT RIGHT',
    left: 'HEAD LEFT',
    right: 'HEAD RIGHT',
  },

  // ── AnnotateView / View / SidePanel ──
  sidePanel: {
    allKeypoints: 'All keypoints',
    visibility: 'Visibility',
    visibilityLabel: (v: 'visible' | 'occluded' | 'out_of_frame'): string =>
      v === 'visible' ? 'visible' : v === 'occluded' ? 'occluded' : 'out_of_frame',
    clear: 'Clear',
    undo: 'Undo',
    undoTitle: 'Revert last change',
    redo: 'Redo',
    redoTitle: 'Redo next change',
    lockAll: 'Lock all',
    revertFrame: 'Revert frame',
    revertFrameTitle: 'Revert all changes on this side at the current frame',
    qualifiedLocked: 'Qualified (locked)',
    lockKeypoint: 'Lock keypoint',
    unlockKeypoint: 'Unlock keypoint',
    lock: 'Lock',
    unlock: 'Unlock',
    revertDialogTitle: 'Revert all changes on this frame?',
    revertDesc: (side: string, frame: number): string =>
      `All manual edits for the ${side} view at frame ${frame} will be discarded. You can undo this with the Undo button.`,
    cancel: 'Cancel',
    revert: 'Revert',
  },

  // ── AnnotateView / View / ZoomPanWrapper ──
  zoom: {
    resetZoom: 'Reset zoom',
  },

  // ── AnnotateView / View / ResizeHandle ──
  resize: {
    resize: (side: string): string => `Resize ${side} side panel`,
    expand: (side: string): string => `Expand ${side} side panel`,
    collapse: (side: string): string => `Collapse ${side} side panel`,
  },

  // ── LoadView / ModeChooser ──
  modeChooser: {
    title: 'Load annotation folder',
    subtitle: 'Pick how you want to load the data.',
    separatelyLabel: 'Load Separately',
    separatelySubtext: 'Drop each file/folder into its own zone',
    altogetherLabel: 'Load Altogether',
    altogetherSubtext: 'Drop a single folder containing everything',
  },

  // ── LoadView / LoadAltogetherView ──
  loadAltogether: {
    back: 'Back',
    modeLabel: 'Load Altogether',
    title: 'Load annotation inputs',
    description: (
      ogEst: React.ReactNode,
      json: React.ReactNode,
      images: React.ReactNode
    ): React.ReactNode => (
      <>
        Select a session folder containing {ogEst}, an optional calibration {json}, and an {images}{' '}
        tree.
      </>
    ),
    selectFolder: 'Select session folder',
    selectFolderHint: (adjust: React.ReactNode): React.ReactNode => (
      <>The folder you pick is also where {adjust} is saved on Submit.</>
    ),
    zoneLabel: 'Session folder',
    zoneHint:
      'Drop a folder containing og_estimations.toon, calibration .json, and images/head_*/ subdirectories',
    loaded: 'Loaded',
    noFilesFound: 'No files found in the selected folder.',
    discovered: 'Discovered',
    frameCount: (label: string, n: number): string => `${label}: ${n} frame${n === 1 ? '' : 's'}`,
    predictions: (file: React.ReactNode): React.ReactNode => <>Predictions: {file}</>,
    calibration: (value: React.ReactNode): React.ReactNode => <>Calibration: {value}</>,
    none: 'none',
    warnings: 'Warnings',
    cannotLoad: 'Cannot load',
    loading: 'Loading…',
    continue: 'Continue',
    warningsDialogTitle: 'Continue with warnings?',
    warningsDialogDescription:
      'The inputs have some inconsistencies. You can still continue — the session will use the data described below.',
    cancel: 'Cancel',
    continueAnyway: 'Continue anyway',
  },

  // ── LoadView / LoadSeparatelyView ──
  loadSeparately: {
    back: 'Back',
    modeLabel: 'Load Separately',
    title: 'Load annotation files',
    description:
      'Front-left and front-right are required. The head_left / head_right pair is optional but all-or-nothing — provide both views or skip both. Frames must be the same type across all loaded views (all videos OR all image folders).',
    filesCount: (n: number): string => `${n} files`,
    frameHint: 'Drop a video file or a folder of images',
    predHint: 'Drop a predictions .json file',
    headLeftFrames: 'Head left frames',
    frontLeftFrames: 'Head front-left frames',
    frontRightFrames: 'Head front-right frames',
    headRightFrames: 'Head right frames',
    headLeftPredictions: 'Head left predictions',
    frontLeftPredictions: 'Head front-left predictions',
    frontRightPredictions: 'Head front-right predictions',
    headRightPredictions: 'Head right predictions',
    picksLabel: 'Picks (optional)',
    picksHint: 'Drop picks.json (or skip)',
    calibrationLabel: 'Calibration (optional)',
    calibrationHint: 'Drop a calibration .json (or skip)',
    cannotContinue: 'Cannot continue',
    loading: 'Loading…',
    continue: 'Continue',
  },

  // ── LoadView / ZoneDropTarget ──
  zoneDrop: {
    skippedAria: 'skipped',
    loadedAria: 'loaded',
    skip: 'Skip',
    clear: 'Clear',
    loaded: 'Loaded',
    skipped: 'Skipped',
    hintJson: 'Drop a .json file here',
    hintToon: 'Drop a .toon file here',
    hintVideoOrFolder: 'Drop a video file or a folder here',
    browse: 'Browse',
  },

  // ── LoadView / shared (draft resume dialog) ──
  draft: {
    title: 'Resume previous session?',
    savedAt: (date: string): string => `A draft for this folder was saved at ${date}.`,
    startFresh: 'Start fresh',
    resume: 'Resume',
  },

  // ── loaders/* — error & validation messages ──
  errors: {
    // video/parser.ts + image/parser.ts (shared)
    predictionsNotValidJson: (msg: string): string => `Predictions JSON is not valid JSON: ${msg}`,

    // video/parser.ts
    missingKeypointScores:
      'Predictions JSON is missing keypoint_scores — the loaded file may have been produced by an older sapiens2 build.',
    missingPositiveFps: 'Predictions JSON is missing a positive numeric "fps".',
    noFramesCannotDetermineLength:
      'Predictions JSON has no frames — cannot determine video length.',
    missingValidImageSize: 'Predictions JSON is missing a valid "image_size" [height, width].',
    missingFramesArray: 'Predictions JSON is missing a "frames" array.',
    frameMalformedKeypoints: (frameIdx: number): string =>
      `Predictions JSON frame ${frameIdx} has malformed "keypoints" array.`,
    frameMissingKeypoint: (frameIdx: number, sapiensIndex: number, name: string): string =>
      `Predictions JSON frame ${frameIdx} is missing keypoint index ${sapiensIndex} (${name}).`,

    // video/index.ts
    invalidVideoFolder: (errs: string): string => `Invalid video folder: ${errs}`,

    // video/validation.ts + image/validation.ts
    missingRequiredFiles: (missing: string): string => `missing required files: ${missing}`,
    incompleteSideViews: (missing: string): string => `incomplete side views; missing: ${missing}`,
    missingNamed: (missing: string): string => `missing ${missing}`,
    noImageFilesIn: (dir: string): string => `no image files in ${dir}/`,

    // image/parser.ts
    missingMetadata: 'Predictions JSON is missing "metadata".',
    metadataFpsInvalid: 'Predictions JSON metadata.fps is missing or non-positive.',
    metadataNumberOfFramesInvalid:
      'Predictions JSON metadata.number_of_frames is missing or non-positive.',
    metadataImageSizeMalformed:
      'Predictions JSON metadata.image_size is malformed (expected [H, W]).',
    predictionsRootNotObject: 'Predictions JSON root is not an object.',
    missingFramesObject: 'Predictions JSON is missing a "frames" object.',

    // image/index.ts
    invalidImageFolder: (errs: string): string => `Invalid image folder: ${errs}`,

    // picks.ts
    picksNotValidJson: (msg: string): string => `picks.json is not valid JSON: ${msg}`,
    picksMustBeArrayOrObject:
      'picks.json must be a JSON array or an object with a frame_indices array.',

    // calibration/index.ts
    calibrationNotValidJson: 'Calibration file is not valid JSON',
    calibrationUnrecognizedFormat:
      'Calibration JSON is not in a recognized format (expected EgoCapture21 or bundle format)',

    // calibration/parse_ds.ts
    dsNoCalibrationsEntries: 'DS calibration: no `calibrations` entries',
    dsNoModelTypeEntry: 'DS calibration: no entry with `model_type: ds` found',
    dsMissingFlFrIntrinsics: (cameras: string): string =>
      `DS calibration: missing FL/FR intrinsics (found cameras: ${cameras})`,
    dsMissingFlFrExtrinsics: 'DS calibration: missing FL/FR extrinsics',

    // calibration/parse_kb.ts
    egoCapture21MissingFields: 'EgoCapture21 JSON missing required fields',

    // calibration/parse_ocam.ts
    bundleNoCalibrationsEntries: 'Bundle JSON has no calibrations entries',
    bundleNoCamerasStereoPairs: 'Bundle JSON: no entry with `cameras` + `stereo_pairs` found',
    bundleCannotFindFlFrCameras: (cameras: string): string =>
      `Bundle JSON: cannot find FL/FR cameras among: ${cameras}`,
    bundleNoStereoPair: (flKey: string, frKey: string): string =>
      `Bundle JSON: no stereo pair found for ${flKey}↔${frKey}`,

    // altogether/assemble.ts
    noEstimationsAtRoot: (filename: string): string =>
      `No ${filename} found at the folder root. ` +
      `Expected ${filename} next to the images/ folder.`,
    sideHasImagesNoEstimations: (sideLabel: string, filename: string): string =>
      `${sideLabel} has images but no matching estimations in ` +
      `${filename} (expected a "${sideLabel}" key).`,
    framesOnDiskWithoutPredictions: (sideLabel: string, count: number): string =>
      `${sideLabel}: ${count} frame${count === 1 ? '' : 's'} on disk without predictions.`,
    noCalibrationLoaded: 'No calibration loaded — epipolar overlays will be disabled.',
    noPredictionsDefaultPose:
      'No og_estimations.toon found — starting in secPose v2: the first frame uses a default pose and later frames carry forward from the previous frame.',

    // altogether/parsePredictions.ts
    predictionsMustBeToon: (fileName: string): string =>
      `Predictions file must end in .toon (got "${fileName}"). The .json sibling is not accepted by Load Altogether — use the .toon variant.`,
    predictionsToonNotValidYaml: (msg: string): string =>
      `Predictions .toon is not valid YAML: ${msg}`,
    predictionsToonRootNotMapping: 'Predictions .toon root is not a mapping.',
    predictionsToonFailedToParseKey: (key: string, msg: string): string =>
      `Predictions .toon: failed to parse "${key}": ${msg}`,

    // altogether/discoverFrames.ts
    missingImagesSubdir:
      'Dropped folder is missing an images/ subdirectory. ' +
      'Expected layout: <folder>/images/head_front_left/, head_front_right/, head_left/, head_right/.',
    imagesMustContainFrontPair:
      'images/ must contain both head_front_left/ and head_front_right/ subdirectories with frame images.',
    sidePairAllOrNothing: (found: string, missing: string): string =>
      `Side pair must be all-or-nothing: found head_${found}/ ` +
      `without head_${missing}/. Provide both, or neither.`,

    // altogether/clipping.ts
    framesOnDiskClipped: (label: string, count: number, clipLimit: number): string =>
      `${label}: ${count} frame${count === 1 ? '' : 's'} on disk — clipped to ${clipLimit}.`,
    predictionsCoverClipped: (label: string, count: number, clipLimit: number): string =>
      `${label}: predictions cover ${count} frame${count === 1 ? '' : 's'} — clipped to ${clipLimit}.`,

    // separate/assemble.ts
    frontRightMustBeSingleVideo:
      'Front-right frames must also be a single video file (front pair must match).',
    frontLeftNoImageFiles: 'Front-left frames folder contains no image files.',
    frontRightNoImageFiles: 'Front-right frames folder contains no image files.',
    frontRightVideoFrontLeftFolderMismatch:
      'Front-right frames is a single video file but front-left frames is a folder (front pair must match).',
    incompleteHeadSideViews:
      'Incomplete head_left / head_right side views — provide all four (head_left frames + predictions, head_right frames + predictions) or none.',
    headLeftMustBeSingleVideo:
      'head_left frames must be a single video file (matching front pair mode).',
    headRightMustBeSingleVideo:
      'head_right frames must be a single video file (matching front pair mode).',
    headLeftNoImageFiles: 'head_left frames folder contains no image files.',
    headRightNoImageFiles: 'head_right frames folder contains no image files.',
  },

  // ── loaders/image/ImageCanvas.tsx ──
  imageCanvas: {
    frameAlt: (side: string): string => `${side} frame`,
  },
};

export type Strings = typeof EN;

const ZH: Strings = {
  topBar: {
    quit: '退出',
    cancel: '取消',
    freeDrag: '自由拖动',
    freeDragTitle: '切换自由拖动 (Shift)',
    skeleton: '骨架',
    unsavedTitle: '未保存的修改',
    unsavedDesc: '您有未保存的修改。仍要退出吗？',
    v2Badge: 'secPose v2 · 沿用上一帧',
    v2BadgeTitle:
      '未加载预测。第一帧使用默认姿态；后续帧在你编辑或保存之前，会从上一帧沿用关节点。',
  },

  progress: {
    ariaLabel: '帧进度',
    totalTime: '总时长：',
    totalFrames: '总帧数：',
    totalPickedFrames: '已选帧数：',
    unannotatedFrames: '未标注的帧数：',
    annotatedUnsavedFrames: '已修改的帧数：',
    annotatedSavedFrames: '已保存（未修改）的帧数：',
  },

  submit: {
    submit: '提交',
    saving: '保存中…',
    ok: '确定',
    errorPrefix: '错误：',
    cannotTitle: '暂时无法提交',
    cannotDesc: (done, total) =>
      `并非所有帧都已审核（已完成 ${done} / ${total}）。请对每一帧点击“下一帧”后再提交。`,
  },

  nextBtn: {
    next: '保存当前帧',
    back: '取消',
    submit: '提交',
    saving: '保存中…',
    errorPrefix: '错误：',
    completionTitle: '所有帧已标注',
    completionDesc: '所有帧均已标注完成，是否提交结果？',
  },

  pending: {
    pending: '暂停',
    saving: '保存中…',
  },

  draftTransfer: {
    exportTitle: '导出加密草稿',
    importTitle: '导入草稿',
    exportDialogTitle: '导出加密草稿',
    exportDialogDesc: '请单独保存恢复码，平台无法找回该恢复码。',
    recoveryCode: '恢复码',
    copyCode: '复制恢复码',
    codeCopied: '恢复码已复制',
    codeSaved: '我已保存恢复码',
    download: '下载加密草稿',
    exported: '加密草稿已下载',
    importDialogTitle: '恢复本地草稿',
    importDialogDesc: '文件只在当前浏览器内解密和校验。',
    chooseFile: '选择草稿文件',
    enterCode: '输入恢复码',
    validate: '校验草稿',
    validating: '校验中…',
    importedDraft: '导入草稿',
    currentDraft: '当前本地草稿',
    noCurrentDraft: '当前没有本地草稿',
    savedAt: '保存时间',
    elapsed: '已标注时长',
    editedFrames: '已编辑帧',
    sourceVerified: '源数据指纹已核对',
    legacyWarning: '旧版明文草稿无法核对 operator 和源数据指纹。提交前请抽查关键帧。',
    overwriteWarning: '恢复后会覆盖当前本地草稿和编辑器状态。',
    cancel: '取消',
    restore: '使用导入草稿',
    restoring: '恢复中…',
    restored: 'SecPose 草稿已恢复到本地',
    leaseRequired: '请先 Claim 当前 Episode，再导入或提交。',
  },

  legend: {
    ariaLabel: '关节点颜色图例',
    shoulder: '肩部',
    elbow: '肘部',
    pelvis: '骨盆',
    leftLine: '左侧线',
    rightLine: '右侧线',
  },

  keypoints: {
    left_shoulder: '左肩',
    right_shoulder: '右肩',
    left_elbow: '左肘',
    right_elbow: '右肘',
    pelvis: '骨盆',
  },

  views: {
    front_left: '头部前左',
    front_right: '头部前右',
    left: '头部左侧',
    right: '头部右侧',
  },

  sidePanel: {
    allKeypoints: '所有关节点',
    visibility: '可见性',
    visibilityLabel: (v) => (v === 'visible' ? '可见' : v === 'occluded' ? '被遮挡' : '不在画面内'),
    clear: '清除',
    undo: '撤销',
    undoTitle: '撤销上一步更改',
    redo: '重做',
    redoTitle: '重做下一步更改',
    lockAll: '全部锁定',
    revertFrame: '还原此帧',
    revertFrameTitle: '还原当前帧此侧的所有更改',
    qualifiedLocked: '已合格（已锁定）',
    lockKeypoint: '锁定关节点',
    unlockKeypoint: '解锁关节点',
    lock: '锁定',
    unlock: '解锁',
    revertDialogTitle: '还原此帧的所有更改？',
    revertDesc: (side, frame) =>
      `${side} 视图在第 ${frame} 帧的所有手动编辑都将被丢弃。你可以使用“撤销”按钮撤回此操作。`,
    cancel: '取消',
    revert: '还原',
  },

  zoom: {
    resetZoom: '重置缩放',
  },

  resize: {
    resize: (side) => `调整 ${side} 侧面板大小`,
    expand: (side) => `展开 ${side} 侧面板`,
    collapse: (side) => `折叠 ${side} 侧面板`,
  },

  modeChooser: {
    title: '加载标注文件夹',
    subtitle: '选择你想要加载数据的方式。',
    separatelyLabel: '分别加载',
    separatelySubtext: '将每个文件/文件夹拖入各自的区域',
    altogetherLabel: '整体加载',
    altogetherSubtext: '拖入一个包含全部内容的文件夹',
  },

  loadAltogether: {
    back: '返回',
    modeLabel: '整体加载',
    title: '加载标注输入',
    description: (ogEst, json, images) => (
      <>
        请选择一个会话文件夹，其中包含 {ogEst}、一个可选的标定 {json} 以及一个 {images} 目录树。
      </>
    ),
    selectFolder: '选择会话文件夹',
    selectFolderHint: (adjust) => <>你所选择的文件夹也是提交时保存 {adjust} 的位置。</>,
    zoneLabel: '会话文件夹',
    zoneHint: '拖入一个包含 og_estimations.toon、标定 .json 以及 images/head_*/ 子目录的文件夹',
    loaded: '已加载',
    noFilesFound: '在所选文件夹中未找到任何文件。',
    discovered: '已发现',
    frameCount: (label, n) => `${label}：${n} 帧`,
    predictions: (file) => <>预测：{file}</>,
    calibration: (value) => <>标定：{value}</>,
    none: '无',
    warnings: '警告',
    cannotLoad: '无法加载',
    loading: '加载中…',
    continue: '继续',
    warningsDialogTitle: '在存在警告的情况下继续？',
    warningsDialogDescription:
      '输入数据存在一些不一致之处。你仍然可以继续 —— 会话将使用下方描述的数据。',
    cancel: '取消',
    continueAnyway: '仍然继续',
  },

  loadSeparately: {
    back: '返回',
    modeLabel: '分别加载',
    title: '加载标注文件',
    description:
      '前左和前右为必填项。head_left / head_right 这一对是可选的，但要么全有要么全无 —— 请同时提供两个视角或同时跳过。在所有已加载的视角中，帧必须是相同类型（全部为视频，或全部为图像文件夹）。',
    filesCount: (n) => `${n} 个文件`,
    frameHint: '拖入一个视频文件或一个图像文件夹',
    predHint: '拖入一个预测 .json 文件',
    headLeftFrames: '头部左侧帧',
    frontLeftFrames: '头部前左帧',
    frontRightFrames: '头部前右帧',
    headRightFrames: '头部右侧帧',
    headLeftPredictions: '头部左侧预测',
    frontLeftPredictions: '头部前左预测',
    frontRightPredictions: '头部前右预测',
    headRightPredictions: '头部右侧预测',
    picksLabel: '挑选 (可选)',
    picksHint: '拖入 picks.json (或跳过)',
    calibrationLabel: '标定 (可选)',
    calibrationHint: '拖入一个标定 .json (或跳过)',
    cannotContinue: '无法继续',
    loading: '加载中…',
    continue: '继续',
  },

  zoneDrop: {
    skippedAria: '已跳过',
    loadedAria: '已加载',
    skip: '跳过',
    clear: '清除',
    loaded: '已加载',
    skipped: '已跳过',
    hintJson: '将 .json 文件拖到此处',
    hintToon: '将 .toon 文件拖到此处',
    hintVideoOrFolder: '将视频文件或文件夹拖到此处',
    browse: '浏览',
  },

  draft: {
    title: '恢复上一个会话？',
    savedAt: (date) => `此文件夹的草稿保存于 ${date}。`,
    startFresh: '重新开始',
    resume: '恢复',
  },

  errors: {
    predictionsNotValidJson: (msg) => `Predictions JSON 不是有效的 JSON：${msg}`,

    missingKeypointScores:
      'Predictions JSON 缺少 keypoint_scores — 加载的文件可能由较旧的 sapiens2 构建生成。',
    missingPositiveFps: 'Predictions JSON 缺少为正数的 "fps"。',
    noFramesCannotDetermineLength: 'Predictions JSON 没有帧 — 无法确定视频长度。',
    missingValidImageSize: 'Predictions JSON 缺少有效的 "image_size" [height, width]。',
    missingFramesArray: 'Predictions JSON 缺少 "frames" 数组。',
    frameMalformedKeypoints: (frameIdx) =>
      `Predictions JSON 第 ${frameIdx} 帧的 "keypoints" 数组格式有误。`,
    frameMissingKeypoint: (frameIdx, sapiensIndex, name) =>
      `Predictions JSON 第 ${frameIdx} 帧缺少关节点索引 ${sapiensIndex}（${name}）。`,

    invalidVideoFolder: (errs) => `无效的视频文件夹：${errs}`,

    missingRequiredFiles: (missing) => `缺少必需文件：${missing}`,
    incompleteSideViews: (missing) => `侧视图不完整；缺少：${missing}`,
    missingNamed: (missing) => `缺少 ${missing}`,
    noImageFilesIn: (dir) => `${dir}/ 中没有图像文件`,

    missingMetadata: 'Predictions JSON 缺少 "metadata"。',
    metadataFpsInvalid: 'Predictions JSON 的 metadata.fps 缺失或非正数。',
    metadataNumberOfFramesInvalid: 'Predictions JSON 的 metadata.number_of_frames 缺失或非正数。',
    metadataImageSizeMalformed: 'Predictions JSON 的 metadata.image_size 格式有误（应为 [H, W]）。',
    predictionsRootNotObject: 'Predictions JSON 的根不是对象。',
    missingFramesObject: 'Predictions JSON 缺少 "frames" 对象。',

    invalidImageFolder: (errs) => `无效的图像文件夹：${errs}`,

    picksNotValidJson: (msg) => `picks.json 不是有效的 JSON：${msg}`,
    picksMustBeArrayOrObject: 'picks.json 必须是 JSON 数组，或包含 frame_indices 数组的对象。',

    calibrationNotValidJson: '标定文件不是有效的 JSON',
    calibrationUnrecognizedFormat: '标定 JSON 不是可识别的格式（应为 EgoCapture21 或 bundle 格式）',

    dsNoCalibrationsEntries: 'DS 标定：没有 `calibrations` 条目',
    dsNoModelTypeEntry: 'DS 标定：未找到带有 `model_type: ds` 的条目',
    dsMissingFlFrIntrinsics: (cameras) => `DS 标定：缺少 FL/FR 内参（找到的相机：${cameras}）`,
    dsMissingFlFrExtrinsics: 'DS 标定：缺少 FL/FR 外参',

    egoCapture21MissingFields: 'EgoCapture21 JSON 缺少必需字段',

    bundleNoCalibrationsEntries: 'Bundle JSON 没有 calibrations 条目',
    bundleNoCamerasStereoPairs: 'Bundle JSON：未找到带有 `cameras` + `stereo_pairs` 的条目',
    bundleCannotFindFlFrCameras: (cameras) => `Bundle JSON：在以下相机中找不到 FL/FR：${cameras}`,
    bundleNoStereoPair: (flKey, frKey) => `Bundle JSON：未找到 ${flKey}↔${frKey} 的立体相机对`,

    noEstimationsAtRoot: (filename) =>
      `在文件夹根目录未找到 ${filename}。` + `应将 ${filename} 放在 images/ 文件夹旁边。`,
    sideHasImagesNoEstimations: (sideLabel, filename) =>
      `${sideLabel} 有图像，但在 ${filename} 中没有匹配的估计结果` +
      `（应包含一个 "${sideLabel}" 键）。`,
    framesOnDiskWithoutPredictions: (sideLabel, count) =>
      `${sideLabel}：磁盘上有 ${count} 帧没有预测结果。`,
    noCalibrationLoaded: '未加载标定 — 极线叠加将被禁用。',
    noPredictionsDefaultPose:
      '未找到 og_estimations.toon — 将以 secPose v2 模式开始：第一帧使用默认姿态，后续帧从上一帧沿用。',

    predictionsMustBeToon: (fileName) =>
      `预测文件必须以 .toon 结尾（收到 "${fileName}"）。Load Altogether 不接受 .json 同名文件 — 请使用 .toon 变体。`,
    predictionsToonNotValidYaml: (msg) => `预测 .toon 不是有效的 YAML：${msg}`,
    predictionsToonRootNotMapping: '预测 .toon 的根不是映射。',
    predictionsToonFailedToParseKey: (key, msg) => `预测 .toon：解析 "${key}" 失败：${msg}`,

    missingImagesSubdir:
      '拖入的文件夹缺少 images/ 子目录。' +
      '应有的布局：<folder>/images/head_front_left/、head_front_right/、head_left/、head_right/。',
    imagesMustContainFrontPair:
      'images/ 必须同时包含 head_front_left/ 和 head_front_right/ 子目录，且其中有帧图像。',
    sidePairAllOrNothing: (found, missing) =>
      `侧视对必须全有或全无：找到 head_${found}/ ` +
      `但缺少 head_${missing}/。请同时提供两者，或都不提供。`,

    framesOnDiskClipped: (label, count, clipLimit) =>
      `${label}：磁盘上有 ${count} 帧 — 已裁剪至 ${clipLimit}。`,
    predictionsCoverClipped: (label, count, clipLimit) =>
      `${label}：预测结果覆盖 ${count} 帧 — 已裁剪至 ${clipLimit}。`,

    frontRightMustBeSingleVideo: '前右帧也必须是单个视频文件（前视对必须匹配）。',
    frontLeftNoImageFiles: '前左帧文件夹不包含任何图像文件。',
    frontRightNoImageFiles: '前右帧文件夹不包含任何图像文件。',
    frontRightVideoFrontLeftFolderMismatch:
      '前右帧是单个视频文件，但前左帧是文件夹（前视对必须匹配）。',
    incompleteHeadSideViews:
      'head_left / head_right 侧视图不完整 — 请提供全部四项（head_left 帧 + 预测，head_right 帧 + 预测），或都不提供。',
    headLeftMustBeSingleVideo: 'head_left 帧必须是单个视频文件（与前视对模式匹配）。',
    headRightMustBeSingleVideo: 'head_right 帧必须是单个视频文件（与前视对模式匹配）。',
    headLeftNoImageFiles: 'head_left 帧文件夹不包含任何图像文件。',
    headRightNoImageFiles: 'head_right 帧文件夹不包含任何图像文件。',
  },

  imageCanvas: {
    frameAlt: (side) => `${side} 帧`,
  },
};

export const t: Strings = LANG === 'zh' ? ZH : EN;
